import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Plus, Edit, Trash2, Loader2, Clock, Settings, X } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminServicesSection = () => {
  const [services, setServices] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [catDialogOpen, setCatDialogOpen] = useState(false);
  const [editingService, setEditingService] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteCat, setDeleteCat] = useState(null);
  const [newCatName, setNewCatName] = useState('');
  const [activeFilter, setActiveFilter] = useState('all');
  const [formData, setFormData] = useState({ name: '', description: '', price: '', duration: '', icon: 'Scissors', category: '' });
  const { getAuthHeaders } = useAuth();

  useEffect(() => { fetchAll(); }, []);

  const fetchAll = async () => {
    try {
      const [svcRes, catRes] = await Promise.all([
        axios.get(`${API}/services`),
        axios.get(`${API}/service-categories`),
      ]);
      setServices(svcRes.data);
      setCategories(catRes.data);
    } catch (error) {
      toast.error('Errore nel caricamento');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = { ...formData, price: parseFloat(formData.price), duration: parseInt(formData.duration) };
      if (editingService) {
        await axios.put(`${API}/services/${editingService.id}`, data, { headers: getAuthHeaders() });
        toast.success('Servizio aggiornato!');
      } else {
        await axios.post(`${API}/services`, data, { headers: getAuthHeaders() });
        toast.success('Servizio creato!');
      }
      setDialogOpen(false);
      resetForm();
      fetchAll();
    } catch (error) {
      toast.error('Errore nel salvataggio');
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await axios.delete(`${API}/services/${deleteTarget}`, { headers: getAuthHeaders() });
      toast.success('Servizio eliminato!');
      fetchAll();
    } catch (error) {
      toast.error('Errore');
    }
    setDeleteTarget(null);
  };

  const handleEdit = (service) => {
    setEditingService(service);
    setFormData({ name: service.name, description: service.description, price: service.price.toString(), duration: service.duration.toString(), icon: service.icon, category: service.category || '' });
    setDialogOpen(true);
  };

  const resetForm = () => {
    setFormData({ name: '', description: '', price: '', duration: '', icon: 'Scissors', category: '' });
    setEditingService(null);
  };

  const handleAddCategory = async () => {
    if (!newCatName.trim()) return;
    try {
      await axios.post(`${API}/service-categories`, { name: newCatName.trim() }, { headers: getAuthHeaders() });
      toast.success('Categoria aggiunta!');
      setNewCatName('');
      fetchAll();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore');
    }
  };

  const handleDeleteCat = async () => {
    if (!deleteCat) return;
    try {
      await axios.delete(`${API}/service-categories/${deleteCat.id}`, { headers: getAuthHeaders() });
      toast.success('Categoria eliminata!');
      setDeleteCat(null);
      if (activeFilter === deleteCat.name) setActiveFilter('all');
      fetchAll();
    } catch (error) {
      toast.error('Errore');
    }
  };

  const filteredServices = activeFilter === 'all' ? services : services.filter(s => s.category === activeFilter);

  if (loading) {
    return <div className="flex items-center justify-center h-48"><Loader2 className="h-8 w-8 animate-spin text-accent" /></div>;
  }

  return (
    <div className="space-y-5" data-testid="services-section">
      <div className="flex items-center justify-between gap-2">
        <h2 className="text-xl sm:text-2xl font-bold">Gestione Servizi</h2>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => setCatDialogOpen(true)} data-testid="manage-svc-categories-btn">
            <Settings className="h-4 w-4 mr-1" /> Categorie
          </Button>
          <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
            <DialogTrigger asChild>
              <Button className="bg-accent text-accent-foreground hover:bg-accent/90" data-testid="add-service-btn">
                <Plus className="h-4 w-4 mr-1" /> Nuovo
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-md">
              <DialogHeader><DialogTitle>{editingService ? 'Modifica Servizio' : 'Nuovo Servizio'}</DialogTitle></DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label>Categoria</Label>
                  <Select value={formData.category} onValueChange={(v) => setFormData({ ...formData, category: v })}>
                    <SelectTrigger data-testid="svc-category-select"><SelectValue placeholder="Scegli categoria" /></SelectTrigger>
                    <SelectContent>
                      {categories.map(c => (<SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Nome Servizio *</Label>
                  <Input required value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} placeholder="Taglio Uomo" className="text-base" data-testid="svc-name-input" />
                </div>
                <div>
                  <Label>Descrizione</Label>
                  <Textarea value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} placeholder="Descrizione..." rows={2} />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div><Label>Prezzo *</Label><Input type="number" step="0.01" required value={formData.price} onChange={(e) => setFormData({ ...formData, price: e.target.value })} placeholder="25" /></div>
                  <div><Label>Durata (min) *</Label><Input type="number" required value={formData.duration} onChange={(e) => setFormData({ ...formData, duration: e.target.value })} placeholder="30" /></div>
                </div>
                <div className="flex gap-3 pt-2">
                  <Button type="button" variant="outline" onClick={() => { setDialogOpen(false); resetForm(); }} className="flex-1">Annulla</Button>
                  <Button type="submit" className="flex-1 bg-accent text-accent-foreground hover:bg-accent/90" data-testid="submit-service-btn">{editingService ? 'Aggiorna' : 'Crea'}</Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Category filter */}
      <div className="flex gap-2 overflow-x-auto scrollbar-hide pb-2">
        <button onClick={() => setActiveFilter('all')} className={`px-3 py-1.5 rounded-full text-sm whitespace-nowrap border transition-all ${activeFilter === 'all' ? 'bg-foreground text-background border-foreground' : 'border-border hover:border-foreground/30'}`} data-testid="svc-filter-all">
          Tutti ({services.length})
        </button>
        {categories.map(c => (
          <button key={c.id} onClick={() => setActiveFilter(c.name)} className={`px-3 py-1.5 rounded-full text-sm whitespace-nowrap border transition-all ${activeFilter === c.name ? 'bg-foreground text-background border-foreground' : 'border-border hover:border-foreground/30'}`} data-testid={`svc-filter-${c.id}`}>
            {c.name} ({services.filter(s => s.category === c.name).length})
          </button>
        ))}
        {services.some(s => !s.category) && (
          <button onClick={() => setActiveFilter('__none')} className={`px-3 py-1.5 rounded-full text-sm whitespace-nowrap border transition-all ${activeFilter === '__none' ? 'bg-foreground text-background border-foreground' : 'border-border hover:border-foreground/30'}`}>
            Senza cat. ({services.filter(s => !s.category).length})
          </button>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {(activeFilter === '__none' ? services.filter(s => !s.category) : filteredServices).map((service) => (
          <Card key={service.id} className="card-elegant" data-testid={`service-${service.id}`}>
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center justify-between text-base">
                <span className="truncate">{service.name}</span>
                <span className="text-accent flex-shrink-0">&euro;{service.price}</span>
              </CardTitle>
              {service.category && <span className="text-xs bg-accent/10 text-accent px-2 py-0.5 rounded-full w-fit">{service.category}</span>}
            </CardHeader>
            <CardContent className="space-y-3">
              {service.description && <p className="text-sm text-muted-foreground">{service.description}</p>}
              <div className="flex items-center gap-1 text-sm text-muted-foreground"><Clock className="h-3 w-3" /><span>{service.duration} min</span></div>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => handleEdit(service)} className="flex-1" data-testid={`edit-service-${service.id}`}><Edit className="h-3 w-3 mr-1" /> Modifica</Button>
                <Button variant="outline" size="sm" className="text-destructive hover:bg-destructive/10 border-destructive/30" onClick={() => setDeleteTarget(service.id)} data-testid={`delete-service-${service.id}`}><Trash2 className="h-3 w-3" /></Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Category Management Dialog */}
      <Dialog open={catDialogOpen} onOpenChange={setCatDialogOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Categorie Servizi</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="flex gap-2">
              <Input value={newCatName} onChange={(e) => setNewCatName(e.target.value)} placeholder="Nuova categoria..." onKeyDown={(e) => e.key === 'Enter' && handleAddCategory()} data-testid="new-svc-cat-input" />
              <Button onClick={handleAddCategory} className="bg-accent text-accent-foreground flex-shrink-0" data-testid="add-svc-cat-btn"><Plus className="h-4 w-4" /></Button>
            </div>
            <div className="space-y-2">
              {categories.map(c => (
                <div key={c.id} className="flex items-center justify-between p-2.5 bg-secondary rounded-lg" data-testid={`svc-cat-item-${c.id}`}>
                  <span className="text-sm font-medium">{c.name}</span>
                  <div className="flex items-center gap-1">
                    <span className="text-xs text-muted-foreground">{services.filter(s => s.category === c.name).length} servizi</span>
                    <Button size="sm" variant="outline" className="h-7 w-7 p-0 text-destructive hover:bg-destructive/10 border-destructive/30" onClick={() => setDeleteCat(c)} data-testid={`delete-svc-cat-${c.id}`}><Trash2 className="h-3 w-3" /></Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Service */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(open) => { if (!open) setDeleteTarget(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader><AlertDialogTitle>Eliminare questo servizio?</AlertDialogTitle><AlertDialogDescription>Questa azione non puo essere annullata.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annulla</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground" data-testid="confirm-delete-service">Elimina</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Category */}
      <AlertDialog open={!!deleteCat} onOpenChange={(open) => { if (!open) setDeleteCat(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader><AlertDialogTitle>Eliminare "{deleteCat?.name}"?</AlertDialogTitle></AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annulla</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteCat} className="bg-destructive text-destructive-foreground" data-testid="confirm-delete-svc-cat">Elimina</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default AdminServicesSection;
